import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { LAYOUT_IDS } from '@modules/profile/component/marketing-page-layout-container/marketing-page-section-layouts.constants';
import {
  extractHeaderMenuLabelItemsFromData,
  HeaderMenuLabelItem,
  normalizeHeaderMenuUrl
} from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout-menu.utils';
import { WhitelabelCoverPageCacheService } from '@modules/whitelabel-cover-page/services/whitelabel-cover-page-cache.service';
import { HEADER_LAYOUT_CONSTANTS } from '@modules/profile/component/marketing-page-layout-container/header-layout/header-layout.constants';
import { CommonService } from '@modules/shared/services/common.service';
import { WHITELABEL_COVER_PAGE_API } from '@modules/whitelabel-cover-page/whitelabel-cover-page.constants';
import type { WhitelabelCoverPageApiData } from '@modules/whitelabel-cover-page/whitelabel-cover-page.interface';

@Injectable({
  providedIn: 'root'
})
export class HeaderMenuLabelService {
  private readonly items$ = new BehaviorSubject<HeaderMenuLabelItem[]>([]);

  readonly itemsStream = this.items$.asObservable();

  constructor(
    private readonly coverPageCache: WhitelabelCoverPageCacheService,
    private readonly commonService: CommonService
  ) {
    this.hydrateFromCache();
  }

  hydrateFromCache(): void {
    this.hydrateFromCoverPage(this.coverPageCache.get());
  }

  setItems(items: Array<{ id?: string; url: string; label: string }>): void {
    const previous = this.items$.value;
    const next = items
      .map((item) => {
        const url = normalizeHeaderMenuUrl(item.url);
        const label = String(item.label ?? '').trim();
        const id = String(item.id ?? '').trim();
        const previousMatch =
          previous.find((entry) => (id && entry.id === id) || (url && entry.url === url)) ??
          null;
        return {
          id: id || previousMatch?.id || '',
          url,
          label
        };
      })
      .filter((item) => item.label && this.isContentFeedItem(item));
    this.items$.next(next);
  }

  getLabelById(id: string, fallback: string): string {
    const key = String(id ?? '').trim();
    if (!key) {
      return fallback;
    }
    const match = this.items$.value.find((item) => item.id === key);
    return match?.label || fallback;
  }

  getLabelByUrl(url: string, fallback: string): string {
    const normalized = normalizeHeaderMenuUrl(url);
    if (!normalized) {
      return fallback;
    }
    const match = this.items$.value.find((item) => item.url === normalized);
    return match?.label || fallback;
  }

  hydrateFromCoverPage(data: WhitelabelCoverPageApiData | null | undefined): void {
    const header = this.findHeaderSection(data);
    if (!header) {
      return;
    }
    const items = extractHeaderMenuLabelItemsFromData(header);
    if (items.length) {
      this.setItems(items);
    }
  }

  private findHeaderSection(
    data: WhitelabelCoverPageApiData | null | undefined
  ): Record<string, unknown> | null {
    const sections = data?.sections;
    if (!Array.isArray(sections)) {
      return null;
    }
    const layoutField = WHITELABEL_COVER_PAGE_API.FIELDS.LAYOUT_ID;
    const match = sections.find((section) => {
      const layoutId = String(
        section?.[layoutField] ?? section?.['section_type'] ?? ''
      ).trim();
      return layoutId === LAYOUT_IDS.HEADER_LAYOUT;
    });
    return match ? (match as Record<string, unknown>) : null;
  }

  private isContentFeedItem(item: HeaderMenuLabelItem): boolean {
    if (item.id === HEADER_LAYOUT_CONSTANTS.MENU_OPTION_IDS.FEEDS) {
      return true;
    }
    const feedUrl = this.getContentFeedUrl();
    return !!feedUrl && item.url === feedUrl;
  }

  private getContentFeedUrl(): string {
    const username =
      this.commonService.getWhiteLabelProperty('user.user_name') ||
      this.commonService.getWhiteLabelConfiguration()?.user?.user_name ||
      this.commonService.getLoggedInUser()?.user_name;
    return typeof username === 'string' && username.trim()
      ? normalizeHeaderMenuUrl(`/${username.trim()}`)
      : '';
  }
}
